import { z } from 'zod';
import { timestampSchema } from './common';

// =============================================================================
// SHARED ENUMS — kept in sync with PlatformDO schema CHECK constraints
// =============================================================================

// `source` = origin/channel: who/what filed the report.
//   sentry        — production runtime error (Sentry webhook)
//   cli           — human via `wayai report create`
//   review        — code agent, during the review loop
//   security_audit — code agent, during the /security-screening skill
export const reportSourceSchema = z.enum(['sentry', 'cli', 'review', 'security_audit']);
export type ReportSource = z.infer<typeof reportSourceSchema>;

// Narrowed source set accepted at the CLI intake endpoint. Excludes `sentry`
// so a `way_`-token caller can't forge a Sentry-origin row.
export const intakeReportSourceSchema = z.enum(['cli', 'review', 'security_audit']);
export type IntakeReportSource = z.infer<typeof intakeReportSourceSchema>;

// `classification` = nature of the report. Drives the GitHub label on escalation
// and the dedup-fingerprint strategy. Orthogonal to `source`.
//   bug | flaky_test | security — defects. Escalate → GH issue → autofix agent, and
//     a `cli` row runs the accept/contest verification loop on issue close.
//   enhancement — NOT a defect: a feature/capability request. Escalated with the
//     `enhancement` GH label, which the triage investigation-spawn step skips (no
//     unattended fixer session — a human picks it up). Its issue-close path skips the
//     verification loop too: even a `cli` enhancement goes straight to `addressed`
//     (there is no "fix" to accept or contest).
export const reportClassificationSchema = z.enum(['bug', 'flaky_test', 'security', 'enhancement']);
export type ReportClassification = z.infer<typeof reportClassificationSchema>;

// `shipped` and `contested` are the agent-interactive loop states (see the report
// lifecycle in the report-triage skill): the fixer marks a fix `shipped` (awaiting the
// reporter), and the reporter `contested` a fix or a dismissal back to triage.
export const reportStatusSchema = z.enum([
  'pending',
  'grouped',
  'escalated',
  'shipped',
  'contested',
  'addressed',
  'dismissed',
]);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

export const reportLocaleSchema = z.enum(['en', 'pt', 'es']);
export type ReportLocale = z.infer<typeof reportLocaleSchema>;

// Author of a `report_message` thread entry. The thread is the reporter-facing narrative
// channel (the loop's memory across rounds); every entry here is reporter-safe — internal
// triage reasoning (`ai_verdict`) is NOT mirrored into it.
export const reportMessageAuthorRoleSchema = z.enum(['reporter', 'triage', 'fixer', 'system']);
export type ReportMessageAuthorRole = z.infer<typeof reportMessageAuthorRoleSchema>;

export const reportMessageSchema = z.object({
  message_id: z.string(),
  author_role: reportMessageAuthorRoleSchema,
  /** The status transition this note accompanied (e.g. 'shipped'); null for a free/system note. */
  transition: z.string().nullable(),
  body: z.string(),
  created_at: timestampSchema,
  /**
   * Visibility: `false` = reporter-facing (the default for every narrative entry); `true` =
   * triage-internal (e.g. a `hold` rationale) — surfaced to platform admins only and NEVER
   * returned on the reporter read path. The reporter's `GET /api/reports/:id` filters these out.
   */
  internal: z.boolean(),
});
export type ReportMessage = z.infer<typeof reportMessageSchema>;

// `sentry_ref_status` = result of cross-checking a Sentry-sourced report's
// captured issue reference (`raw_payload.issue_id` / `short_id`) against the
// live Sentry API at escalation time. Null for non-sentry reports or before the
// check has run. Gates the GH-webhook auto-resolve: only a `valid` reference is
// resolved on issue close, so a fabricated/dead/mismatched ref can never resolve
// the wrong live issue (the failure #2438 documents).
//   valid       — issue_id resolves AND the captured short_id matches the live issue
//   not_found   — issue_id does not resolve (dead/fabricated reference)
//   mismatch    — issue_id resolves but the captured short_id ≠ the live short_id
//   unverified  — could not check (no SENTRY_API_TOKEN, or a Sentry API error)
export const sentryRefStatusSchema = z.enum(['valid', 'not_found', 'mismatch', 'unverified']);
export type SentryRefStatus = z.infer<typeof sentryRefStatusSchema>;

// =============================================================================
// INTAKE — POST /api/reports (CLI only)
//
// Replaces /api/bug-reports. MCP `report_bug` tool has been removed entirely;
// reporting is a code-agent (CLI) workflow.
// =============================================================================

export const createReportBody = z.object({
  title: z.string().min(5).max(256),
  description: z.string().min(10).max(5000),
  steps_to_reproduce: z.string().max(3000).optional(),
  error_message: z.string().max(2000).optional(),

  // Taxonomy (server defaults: source=cli, classification=bug when absent).
  source: intakeReportSourceSchema.optional(),
  classification: reportClassificationSchema.optional(),
  // Stable dedup key (e.g. "<file>::<test>" for flaky, "<file>:<line>" for
  // security). When present, the fingerprint uses it instead of the body hash.
  dedup_key: z.string().min(1).max(256).optional(),

  // Context fields (auto-captured by the CLI)
  organization_id: z.string().optional(),
  hub_id: z.string().uuid().optional(),
  hub_name: z.string().max(256).optional(),
  conversation_id: z.string().uuid().optional(),
  agent_id: z.string().uuid().optional(),
  agent_name: z.string().max(256).optional(),
  client_version: z.string().max(64).optional(),
  additional_context: z.string().max(2000).optional(),
});
export type CreateReportBody = z.infer<typeof createReportBody>;

export interface CreateReportResponse {
  report_id: string;
  status: ReportStatus;
  /** True if this submission created a new row; false if it merged into an existing fingerprint. */
  created: boolean;
  /**
   * Present (with `created: false`) when the matched row had already gone terminal — the same
   * failure came back. `reopened` is true when it returned to `pending` for re-triage, false
   * when a FINAL dismissal held and only the recurrence counter moved.
   */
  recurrence?: { reopened: boolean };
}

// =============================================================================
// EDIT — PATCH /api/reports/:id (reporter) and PATCH /api/admin/reports/:id (admin)
//
// Amends the mutable content fields of an already-filed report. The fields live
// inside `raw_payload`; the dedup `fingerprint` is FROZEN at creation and is
// never recomputed on edit (editing the same 5 fields the fingerprint hashes
// would otherwise let an edit collide with another row's fingerprint or silently
// change the dedup identity). `.strict()` so nothing outside these 5 keys can
// reach the merge; `.refine` rejects a no-op edit (empty body). The 3 optional
// fields accept an empty string to clear them (stored as null, matching create).
// =============================================================================

export const editReportBody = z
  .object({
    title: z.string().min(5).max(256).optional(),
    description: z.string().min(10).max(5000).optional(),
    steps_to_reproduce: z.string().max(3000).optional(),
    error_message: z.string().max(2000).optional(),
    additional_context: z.string().max(2000).optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, {
    message: 'at least one field must be provided',
  });
export type EditReportBody = z.infer<typeof editReportBody>;

export interface EditReportResponse {
  report_id: string;
  status: ReportStatus;
  /** Canonical timestamp of this edit (also stamped on the row's edited_at column). */
  edited_at: string | null;
  /** The merged content blob after the edit. */
  raw_payload: unknown;
}

// =============================================================================
// REPORTER — read-back + verification loop (own `cli` reports only)
//
// GET /api/reports          — list the caller's own reports
// GET /api/reports/:id      — one own report + its message thread
// POST /api/reports/:id/accept   — shipped → addressed (accept the fix)
// POST /api/reports/:id/contest  — {shipped|dismissed} → contested (reason required)
//
// The reporter-facing summary deliberately WITHHOLDS internal fields (`gh_issue_url`,
// `ai_verdict`) — the private GitHub URL is never surfaced to a reporter (same discipline as
// the escalation email), and triage's internal reasoning stays internal. Reporter-facing
// authored content reflects the current title/description in `raw_payload`; transition
// narrative (fixer's shipped note, dismissal reason, contest reasons, system notices) lives in
// the `messages` thread.
// =============================================================================

export const reporterReportSummarySchema = z.object({
  report_id: z.string(),
  title: z.string(),
  description: z.string(),
  classification: reportClassificationSchema,
  status: reportStatusSchema,
  created_at: timestampSchema,
  triaged_at: timestampSchema.nullable(),
  resolved_at: timestampSchema.nullable(),
  /** How many times the reporter has contested (gates the hard cap). */
  contest_count: z.number(),
  /** True once triage issued a non-contestable (final) dismissal. */
  dismissal_final: z.boolean(),
});
export type ReporterReportSummary = z.infer<typeof reporterReportSummarySchema>;

export const reporterListResponseSchema = z.object({
  data: z.array(reporterReportSummarySchema),
});
export type ReporterListResponse = z.infer<typeof reporterListResponseSchema>;

export const getReportResponseSchema = z.object({
  report: reporterReportSummarySchema,
  messages: z.array(reportMessageSchema),
});
export type GetReportResponse = z.infer<typeof getReportResponseSchema>;

/** Returned by /accept and /contest — the updated reporter-facing summary. */
export const reporterTransitionResponseSchema = z.object({
  report: reporterReportSummarySchema,
});
export type ReporterTransitionResponse = z.infer<typeof reporterTransitionResponseSchema>;

export const contestReportBody = z.object({
  reason: z.string().min(1).max(5000),
});
export type ContestReportBody = z.infer<typeof contestReportBody>;

export const reporterListQuery = z.object({
  status: reportStatusSchema.optional(),
});
export type ReporterListQuery = z.infer<typeof reporterListQuery>;

// Hard cap on reporter contests (across both fix- and dismissal-disputes). Once
// `contest_count` reaches this, the next contest is refused (409) and the report is flagged
// for human routing — this is what bounds the converge loop now that `dismissed` is
// contestable. Shared so the route, the DO-adjacent logic, and tests agree on one number.
export const REPORT_CONTEST_CAP = 3;

// =============================================================================
// ADMIN — read views
// =============================================================================

export const listReportsQuery = z.object({
  status: reportStatusSchema.optional(),
  source: reportSourceSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  /** ISO 8601 lower bound on created_at. */
  since: z.string().datetime().optional(),
  /**
   * ISO 8601 lower bound on resolution time — "what did we resolve recently", which `since`
   * (a created_at filter) cannot answer for a long-lived report resolved yesterday. Implies
   * a terminal status (`addressed`/`dismissed`) and orders on that same axis.
   */
  resolved_since: z.string().datetime().optional(),
});
export type ListReportsQuery = z.infer<typeof listReportsQuery>;

export interface AdminReportSummary {
  report_id: string;
  source: ReportSource;
  classification: ReportClassification;
  status: ReportStatus;
  fingerprint: string;
  group_id: string | null;
  gh_issue_url: string | null;
  ai_verdict: string | null;
  dismissal_reason: string | null;
  /** Live-Sentry cross-check result for sentry-sourced reports (set at escalation). Null otherwise. */
  sentry_ref_status: SentryRefStatus | null;
  user_id: string | null;
  org_id: string | null;
  reporter_email: string | null;
  reporter_locale: string | null;
  created_at: string;
  triaged_at: string | null;
  resolved_at: string | null;
  /** Set when a content edit amended this report; null if never edited. */
  edited_at: string | null;
  /** user_id of the last editor (reporter or platform admin); null if never edited. */
  edited_by: string | null;
  /** Times the reporter contested a fix or dismissal (gates the hard cap). */
  contest_count: number;
  /** True once triage issued a non-contestable (final) dismissal. */
  dismissal_final: boolean;
  /** Times triage held (deferred) this row by a cycle — the explicit "how many cycles" signal. */
  hold_count: number;
  /** Canonical timestamp of the most recent hold; null if never held. */
  last_held_at: string | null;
  /** Times this fingerprint re-arrived after the row had gone terminal (the regression signal). */
  recurrence_count: number;
  /** Canonical timestamp of the most recent recurrence; null if it has never recurred. */
  last_recurred_at: string | null;
  /** The GitHub issue this row carried before a recurrence reopened it; null if never reopened. */
  previous_gh_issue_url: string | null;
}

export interface AdminReportDetail extends AdminReportSummary {
  raw_payload: unknown;
  group_members: AdminReportSummary[];
  notified_at_escalated: string | null;
  notified_at_addressed: string | null;
  notified_at_dismissed: string | null;
  notified_at_shipped: string | null;
  /** Reporter-facing narrative thread (also visible to admins). */
  messages: ReportMessage[];
}

export interface AdminReportListResponse {
  data: AdminReportSummary[];
  total: number;
}

// =============================================================================
// ADMIN — mutations (state-machine RPC)
// =============================================================================

// z.string().url() accepts `javascript:` — constrain to https://github.com/<owner>/<repo>/issues/<N> for email-safe href.
export const GITHUB_ISSUE_URL_REGEX = /^https:\/\/github\.com\/[^/]+\/[^/]+\/issues\/\d+(?:[/?#]|$)/;
const githubIssueUrlSchema = z
  .string()
  .url()
  .refine((u) => GITHUB_ISSUE_URL_REGEX.test(u), {
    message: 'must be an https://github.com/<owner>/<repo>/issues/<number> URL',
  });

// Admin /transition surface (triage + fixer). Note: `contested` is intentionally absent —
// only the reporter sets it, via the dedicated reporter-owned /accept and /contest endpoints
// (a reporter MUST NOT reach the admin adjudication targets). `shipped` is the fixer's
// hand-back with a reporter-facing `note`; `final` makes a dismissal non-contestable.
export const transitionReportBody = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('escalated'),
    gh_issue_url: githubIssueUrlSchema,
    ai_verdict: z.string().max(2000).optional(),
  }),
  z.object({
    target: z.literal('shipped'),
    note: z.string().min(1).max(2000).optional(),
  }),
  z.object({
    target: z.literal('addressed'),
  }),
  z.object({
    target: z.literal('dismissed'),
    dismissal_reason: z.string().min(1).max(5000),
    ai_verdict: z.string().max(2000).optional(),
    final: z.boolean().optional(),
  }),
]);
export type TransitionReportBody = z.infer<typeof transitionReportBody>;

export interface TransitionReportResponse {
  report: AdminReportSummary;
  cascaded_report_ids: string[];
}

export const groupReportsBody = z.object({
  member_ids: z.array(z.string().uuid()).min(1).max(100),
});
export type GroupReportsBody = z.infer<typeof groupReportsBody>;

/**
 * A member that could not be absorbed into the group, with the reason:
 * - `not_found` — member id does not exist
 * - `terminal` — member is already `addressed`/`dismissed` (detail = the status)
 * - `grouped_elsewhere` — member already belongs to another canonical (detail = its group_id)
 * - `different_gh_issue` — member is linked to a GitHub issue other than the
 *   canonical's (detail = the member's gh_issue_url); absorbing it would orphan that issue
 */
export interface GroupReportSkip {
  report_id: string;
  reason: 'not_found' | 'terminal' | 'grouped_elsewhere' | 'different_gh_issue';
  detail?: string;
}

export interface GroupReportsResponse {
  canonical_id: string;
  grouped: number;
  skipped: GroupReportSkip[];
}

// =============================================================================
// ADMIN — ungroup (detach a grouped member, inverse of group)
// =============================================================================

/**
 * Response for POST /api/admin/reports/:id/ungroup. The detached member is
 * returned to `pending` with its `group_id` cleared. Note: grouping cleared the
 * member's `gh_issue_url` (the canonical owns the issue), so detach cannot
 * restore it — the member re-enters triage as if freshly filed.
 */
export interface UngroupReportResponse {
  report: AdminReportSummary;
}

// =============================================================================
// ADMIN — hold (defer a decision one triage cycle)
//
// Records "I looked at this `pending` row and chose to wait one more cycle" without
// transitioning it: bumps `hold_count` + `last_held_at` (the explicit cycle counter) and
// appends a triage-internal note (the rationale) to the message thread. Silent for ALL
// sources — never emails, even a `cli` row — and the note is internal (reporter never sees
// it). The reason is required: a hold with no recorded rationale defeats the purpose.
// =============================================================================

export const holdReportBody = z.object({
  reason: z.string().min(1).max(5000),
});
export type HoldReportBody = z.infer<typeof holdReportBody>;

export interface HoldReportResponse {
  report: AdminReportSummary;
}

// =============================================================================
// REPORT TRANSITION STATE MACHINE — validity matrix (single source of truth)
// =============================================================================

export type ReportTransitionTarget =
  | 'escalated'
  | 'shipped'
  | 'contested'
  | 'addressed'
  | 'dismissed';

// Statuses from which a given target is a hard reject (operator error). Excludes
// idempotent no-ops (target already reached, or escalate-from-addressed), which
// `transitionReport` short-circuits to a no-op rather than rejecting. Mirrors the
// branch guards in `PlatformDO.transitionReport` — kept here as the one place
// both the route (to return a clean 4xx) and the DO (as a backstop) agree on.
//
// Allowed (non-reject, non-no-op) sources per target, for the agent-interactive loop:
//   escalated ← pending (first triage) · contested (triage re-escalates); addressed is an
//               idempotent no-op (not a real re-escalation)
//   shipped   ← escalated (fixer marks the fix shipped)
//   contested ← shipped (reporter rejects the fix) · dismissed (reporter disputes the dismissal)
//   addressed ← escalated (non-cli, OR any-source `enhancement`, auto-close) · shipped (reporter accepts) · contested (triage agrees)
//   dismissed ← pending (untriaged) · escalated (false-positive) · contested (dispute rejected)
const REPORT_TRANSITION_REJECT: Record<ReportTransitionTarget, ReadonlySet<ReportStatus>> = {
  escalated: new Set<ReportStatus>(['grouped', 'shipped', 'dismissed']),
  shipped: new Set<ReportStatus>(['pending', 'grouped', 'contested', 'addressed', 'dismissed']),
  contested: new Set<ReportStatus>(['pending', 'grouped', 'escalated', 'addressed']),
  addressed: new Set<ReportStatus>(['pending', 'grouped', 'dismissed']),
  dismissed: new Set<ReportStatus>(['grouped', 'shipped', 'addressed']),
};

/**
 * Returns an operator-facing rejection message if transitioning a report in
 * `status` to `target` is invalid, or `null` if it is allowed (including
 * idempotent no-ops). Pure — no I/O. The route throws ConflictError(409) on a
 * non-null result so an invalid transition surfaces as a clean 4xx instead of a
 * 500 (a plain Error thrown inside the DO loses its AppError brand across RPC).
 */
export function getReportTransitionRejection(
  report: { status: ReportStatus; group_id?: string | null },
  target: ReportTransitionTarget,
): string | null {
  if (!REPORT_TRANSITION_REJECT[target].has(report.status)) return null;
  if (report.status === 'grouped') {
    const ref = report.group_id ? ` under ${report.group_id}` : '';
    return (
      `Report is grouped${ref} and cannot be ${target} directly. ` +
      `Detach it first (\`wayai admin report ungroup <id>\`), or transition the ` +
      `canonical so the outcome cascades to its members.`
    );
  }
  return `Report cannot be ${target} from status '${report.status}'.`;
}

// =============================================================================
// REPORT EDIT — non-terminal-state gate (single source of truth)
// =============================================================================

export type ReportEditRole = 'reporter' | 'admin';

// Statuses from which a content edit is allowed, by role. A reporter may only
// fix their report before triage (`pending`); once it has been escalated into a
// GitHub issue, only a platform admin may refine it (`escalated`). `grouped`
// (duplicate, subsumed by a canonical) and the terminal states are frozen for
// everyone. The DO uses the `admin` set as its data-integrity backstop — the
// per-role narrowing is the route's authorization concern.
const REPORT_EDITABLE_STATUSES: Record<ReportEditRole, ReadonlySet<ReportStatus>> = {
  reporter: new Set<ReportStatus>(['pending']),
  admin: new Set<ReportStatus>(['pending', 'escalated']),
};

/**
 * Returns an operator-facing rejection message if editing a report in `status`
 * is not allowed for `role`, or `null` if the edit is permitted. Pure — no I/O.
 * Both edit routes throw ConflictError(409) on a non-null result; the DO's
 * `editReportContent` calls it with role `'admin'` (most permissive) as a
 * backstop so a terminal/grouped row can never be mutated at the data layer.
 */
export function getReportEditRejection(
  report: { status: ReportStatus; group_id?: string | null },
  role: ReportEditRole,
): string | null {
  if (REPORT_EDITABLE_STATUSES[role].has(report.status)) return null;
  if (report.status === 'grouped') {
    const ref = report.group_id ? ` under ${report.group_id}` : '';
    return (
      `Report is grouped${ref} and cannot be edited directly. ` +
      `Detach it first (\`wayai admin report ungroup <id>\`) or edit the canonical.`
    );
  }
  if (role === 'reporter' && report.status === 'escalated') {
    return (
      `Report has been escalated to a GitHub issue and can no longer be edited ` +
      `by the reporter; contact a platform admin to refine it.`
    );
  }
  return `Report cannot be edited from status '${report.status}'.`;
}

// =============================================================================
// REPORT HOLD — defer-one-cycle gate (single source of truth)
// =============================================================================

// A hold only makes sense before the first triage decision: it defers escalate/dismiss by a
// cycle. Once a row has moved past `pending` (grouped under a canonical, escalated into an
// issue, in the agent-interactive loop, or terminal) there is no "wait one cycle" decision left
// to defer — re-adjudicate it through the proper transition instead.
const REPORT_HOLDABLE_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>(['pending']);

/**
 * Returns an operator-facing rejection message if a `pending`-only hold is not allowed for a
 * report in `status`, or `null` if the hold is permitted. Pure — no I/O. The route throws
 * ConflictError(409) on a non-null result; the DO's `holdReport` calls it as a data-integrity
 * backstop so a non-pending row can never accrue a phantom hold.
 */
export function getReportHoldRejection(report: { status: ReportStatus; group_id?: string | null }): string | null {
  if (REPORT_HOLDABLE_STATUSES.has(report.status)) return null;
  if (report.status === 'grouped') {
    const ref = report.group_id ? ` under ${report.group_id}` : '';
    return (
      `Report is grouped${ref} and cannot be held — it is a duplicate subsumed by its canonical. ` +
      `Hold (or transition) the canonical instead.`
    );
  }
  return `Report cannot be held from status '${report.status}' — hold only defers a 'pending' row by a cycle.`;
}

// =============================================================================
// REPORT RECURRENCE — what intake does when a fingerprint re-arrives
// =============================================================================

/**
 * What `createReport` does when its `ON CONFLICT(fingerprint)` lands on an existing row.
 *
 * - `reopen`      — the row had gone terminal and the same failure is back: a regression.
 *                   Return it to `pending` so it re-enters the triage sweep, carrying the
 *                   recurrence counters and a pointer to the issue that already closed once.
 * - `record_only` — count the recurrence, change nothing else. Reserved for a dismissal
 *                   triage marked FINAL: the operator settled it deliberately, and intake
 *                   must not resurrect that decision behind their back. The counter still
 *                   climbs so a wrongly-final row becomes visible rather than silent.
 * - `none`        — the row is still in flight (`pending`/`grouped`/`escalated`/`shipped`/
 *                   `contested`). Plain dedup: the occurrence is already represented.
 */
export type ReportRecurrenceOutcome = 'reopen' | 'record_only' | 'none';

/**
 * Statuses a report can never leave on its own. The recurrence rule below is the ONLY thing
 * that returns one to the queue, so every consumer that asks "is this row finished?" reads it
 * from here rather than re-spelling the pair.
 */
export const REPORT_TERMINAL_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>([
  'addressed',
  'dismissed',
]);

/**
 * How far intake may act on a fingerprint collision, decided by WHO is reporting.
 *
 * - `trusted`  — the caller's identity is established by transport, not by a request body
 *                (today: the signature-verified Sentry webhook). May reopen any row.
 * - `reporter` — an `authMiddleware`-authenticated user calling `POST /api/reports`. The
 *                request body supplies `classification` and `dedup_key`, so the CALLER
 *                chooses which row to collide with — the fingerprint is not a capability.
 */
export type ReportRecurrenceAuthority = 'trusted' | 'reporter';

/**
 * Decides the recurrence outcome for a row an incoming report just collided with. Pure — no
 * I/O; `PlatformDO.createReport` is the only caller and owns the writes for each branch.
 *
 * Why terminal rows reopen at all: the fingerprint index is UNIQUE and NOT status-scoped, so
 * without this an identical failure arriving after its issue closed would `DO NOTHING` and
 * vanish — the loudest possible regression signal, silently swallowed, invisible to a triage
 * sweep that only reads the non-terminal cohorts.
 *
 * Why a `reporter` caller may only reopen its OWN row: the colliding row is selected purely by
 * `fingerprint`, which is derived from caller-supplied `classification` + `dedup_key`, and the
 * documented key shapes (`<file>::<test>`, `<file>:<line>`) are guessable. A reopen nulls
 * `gh_issue_url`, clears the notification slots and resets the contest budget, so without this
 * gate any authenticated user could resurrect, mute and re-notify a report belonging to someone
 * else. An unauthorized collision falls back to `none` — byte-for-byte the pre-recurrence
 * behaviour (`ON CONFLICT DO NOTHING`), so this adds no reachable state change at all.
 * Ownership is the reporting principal: `user_id`. Sentry rows carry none and are unreachable
 * from `POST /api/reports` anyway (`intakeReportSourceSchema` excludes `sentry`).
 */
export function getReportRecurrenceOutcome(
  report: {
    status: ReportStatus;
    dismissal_final: boolean;
    user_id?: string | null;
  },
  caller: { authority: ReportRecurrenceAuthority; user_id?: string | null } = {
    authority: 'reporter',
  },
): ReportRecurrenceOutcome {
  if (!REPORT_TERMINAL_STATUSES.has(report.status)) return 'none';
  if (caller.authority !== 'trusted') {
    // Not the row's own reporter → treat the collision as plain dedup.
    if (!report.user_id || !caller.user_id || report.user_id !== caller.user_id) return 'none';
  }
  if (report.status === 'dismissed' && report.dismissal_final) return 'record_only';
  return 'reopen';
}
